import {
  Button,
  FormCheckbox,
  FormInput,
  FormPassword,
  FormRoot,
  Loader,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { LogInIcon } from "lucide-react";
import { useState } from "react";
import z from "zod";
import { authClient } from "../api/client";
import { useAuthState } from "../model/useAuthState";

const SignInFormSchema = z.object({
  email: z.string().email({ message: "ອີເມວບໍ່ຖືກຕ້ອງ" }),
  password: z.string().min(6, { message: "ລະຫັດຜ່ານຕ້ອງຢ່າງນ້ອຍ 8 ຕົວອັກສອນ" }),
  rememberMe: z.boolean().optional(),
});

type ISignInFormSchema = z.infer<typeof SignInFormSchema>;

export default function SignInForm() {
  const navigate = useNavigate({ from: "/" });
  const { isLoading: sessionLoading } = useAuthState();
  const [signingIn, setSigningIn] = useState(false);

  const form = RHF.useForm({
    defaultValues: { email: "", password: "", rememberMe: false },
    resolver: zodResolver(SignInFormSchema),
  });

  const submitLoading = signingIn || form.formState.isSubmitting;

  const handleSubmit = async (value: ISignInFormSchema) => {
    setSigningIn(true);
    try {
      await authClient.signIn.email(
        { email: value.email, password: value.password },
        {
          onSuccess: () => {
            navigate({ to: "/app/dashboard" });
            toast.success("ເຂົ້າລະບົບສໍາເລັດ");
          },
          onError: (error) => {
            toast.error(error.error.message || error.error.statusText);
          },
        },
      );
    } finally {
      setSigningIn(false);
    }
  };

  if (sessionLoading) {
    return <Loader />;
  }

  return (
    <FormRoot<ISignInFormSchema> methods={form} onSubmit={handleSubmit}>
      <fieldset className="space-y-4" disabled={submitLoading}>
        <FormInput
          name="email"
          label="ອີເມວ"
          requiredMark
          placeholder="name@example.com"
          autoComplete="email"
        />
        <FormPassword
          name="password"
          label="ລະຫັດຜ່ານ"
          requiredMark
          placeholder="********"
          autoComplete="current-password"
        />

        <FormCheckbox name="rememberMe" label="ຈໍາຂ້ອຍໄວ້" />
      </fieldset>

      <Button
        type="submit"
        isLoading={submitLoading}
        className="mt-6 w-full gap-2"
        size="lg"
      >
        {!submitLoading ? <LogInIcon className="size-4 shrink-0" /> : null}
        ເຂົ້າລະບົບ
      </Button>
    </FormRoot>
  );
}
